import { selectBlockParent } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { stopPropagation } from '@/helpers'
import type { AppState } from '@/types'
import { clsJoin } from '@/utils'
import { setPreventPast } from '@/utils/clipboard'
import lightOrDark from '@/utils/color/lightOrDark'
import { replaceArrow } from '@/utils/replaceArrow'
import useBlockActions from '../../Blocks/hooks/useBlockActions'
import css from './styles.module.scss'

type Props = {
  blockId: string
  completed: boolean
  themeBold?: boolean
  onClick?: () => void
}

export const Name: React.FC<Props> = ({
  blockId,
  completed,
  themeBold,
  onClick,
}) => {
  const dispatch = useDispatch()

  const ref = useRef<HTMLTextAreaElement>(null)
  const [focus, setFocus] = useState(false)

  const { title: name, colorId } = useSelector(
    migrationProjectSelector.getProjectBlockOrSymbolData(blockId),
    shallowEqual
  )

  const blockParentNode = useSelector(selectBlockParent(blockId), shallowEqual)
  const nodeId = blockParentNode[0] || ''

  const colorHex = useSelector(
    migrationProjectSelector.getProjectColorForUI(colorId)
  )

  const editableValue = useSelector<AppState, string>(({ canvas }) => {
    if (canvas.block.selectedToEditBlockId === blockId) {
      return canvas.block.editTitleValue
    }

    return ''
  })

  const isEditable = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.block.selectedToEditBlockId === blockId
  )

  const { onBlockInputKeyDown } = useBlockActions()

  // @ts-expect-error
  const onInput = ({ target }) => {
    resize(target)
  }

  // @ts-expect-error
  const onChange = ({ target }) => {
    const value = replaceArrow(target.value)

    dispatch({
      type: CanvasActionTypes.EDIT_BLOCK_TITLE_VALUE,
      payload: value,
    })
  }

  const onFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.target.setSelectionRange(e.target.value.length, e.target.value.length)
  }

  const onBlur = () => {
    if (focus && isEditable) {
      dispatch({
        type: CanvasActionTypes.RESET_EDIT_BLOCK,
      })
    }
  }

  // @ts-expect-error
  const resize = (el) => {
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  useEffect(() => {
    if (isEditable && ref.current) {
      resize(ref.current)

      // @to-do review, safari hack
      setTimeout(() => {
        ref?.current?.focus()
        setFocus(true)
      }, 10)
    }
  }, [ref, isEditable])

  const classNames = clsJoin(
    css.container,
    completed && css.completed,
    lightOrDark(colorHex) === 'light' && css.light,
    themeBold && css.bold
  )

  return (
    <div className={classNames} onClick={onClick}>
      {isEditable && (
        <textarea
          ref={ref}
          rows={1}
          className={css.input}
          placeholder="Block name"
          data-testid="block-name"
          value={editableValue}
          onInput={onInput}
          onChange={onChange}
          onClick={stopPropagation}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={(e) => onBlockInputKeyDown(e, blockId, nodeId)}
          onPaste={(e) => {
            if (setPreventPast(e.clipboardData.getData('text'))) {
              e.preventDefault()
            }
          }}
        />
      )}

      {!isEditable && name && <div className={css.text}>{name}</div>}
    </div>
  )
}
